import { Form, Formik } from "formik";
import { handleSubmitForm } from "helpers/apiUtils";
import { useQueryParam } from "hooks/useQueryParam";
import { useSmartBack } from "hooks/useSmartBack";
import { Row, Col, Spinner } from "react-bootstrap";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getVehicleColorValidationSchema } from "utils/validations";
import CustomField from "components/form/CustomField";
import { usePermission } from "hooks/usePermission";
import CustomColorField from "components/form/CustomColorField";
import {
  addVehicleColor,
  getVehicleColorDetails,
  updateVehicleColor,
} from "store/slices/vehicleColorSlice";
import PageHeader from "components/common/PageHeader";

function AddEditVehicleColor() {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const id = useQueryParam("id");
  const isEdit = Boolean(id);
  const goBack = useSmartBack();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { canAction } = usePermission();
  const { canAdd, canEdit } = canAction("vehicles");

  const {
    isLoading,
    loading,
    itemDetails: vehicleColorDetails,
  } = useSelector((state) => state.vehicleColor);

  const [initialValues, setInitialValues] = useState({
    name: {
      en: "",
      ar: "",
    },
    code: "",
  });

  useEffect(() => {
    if (isEdit && id) {
      dispatch(getVehicleColorDetails(id));
    }
  }, [isEdit, id, dispatch]);

  useEffect(() => {
    if (isEdit && vehicleColorDetails) {
      setInitialValues({
        name: {
          en: vehicleColorDetails?.name?.en || "",
          ar: vehicleColorDetails?.name?.ar || "",
        },
        code: vehicleColorDetails?.code || "",
      });
    }
  }, [isEdit, vehicleColorDetails]);

  const handleSubmit = (values) => {
    const payload = {
      name: {
        en: values.name.en,
        ar: values.name.ar,
      },
      code: values.code,
    };

    if (isEdit) {
      const updatedVehicleModelPayload = {
        id,
        ...values,
      };
      handleSubmitForm({
        dispatch,
        action: updateVehicleColor,
        payload: updatedVehicleModelPayload,
        onSuccess: () => navigate(-1),
      });
    } else {
      handleSubmitForm({
        dispatch,
        action: addVehicleColor,
        payload,
        onSuccess: () => navigate(-1),
      });
    }
  };
  if (!canEdit && isEdit) return null;
  if (!canAdd && !isEdit) return null;

  if (isEdit && loading)
    return (
      <div className="g_center my-5">
        <Spinner />;
      </div>
    );

  return (
    <div className="page-content">
      <PageHeader
        isBackBtn={true}
        title={isEdit ? t("Edit Vehicle Color") : t("Add Vehicle Color")}
      />

      <div className="common_card">
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={getVehicleColorValidationSchema(t, isEdit)}
          onSubmit={handleSubmit}
        >
          {() => (
            <Form>
              <Row>
                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      label={t("Name EN")}
                      name="name.en"
                      placeholder={t("Enter Name EN")}
                      enableTranslate
                      translateTo="ar"
                      targetField="name.ar"
                    />
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      label={t("Name AR")}
                      name="name.ar"
                      placeholder={t("Enter Name AR")}
                      className="rtl"
                      enableTranslate
                      translateTo="en"
                      targetField="name.en"
                    />
                  </div>
                </Col>
                <Col lg={6}>
                  <div className="form_field">
                    <CustomColorField name="code" label={t("Color Code")} />
                  </div>
                </Col>

                <Col lg={12} className="d-flex justify-content-end gap-2 mt-3">
                  <button
                    type="button"
                    className="main_btn outline"
                    onClick={goBack}
                  >
                    {t("Cancel")}
                  </button>

                  <button
                    type="submit"
                    className="main_btn"
                    disabled={isLoading}
                  >
                    {isEdit ? t("Save Changes") : t("Create Vehicle Color")}
                  </button>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
}

export default AddEditVehicleColor;
